import { Alert, Group, Text, Button, Tooltip } from '@mantine/core';
import { CheckCircle, AlertCircle, Send } from 'react-feather';
import type { CoachCalloutData } from '../../../../services/api/cohortCoaches';

interface GroupCalloutBannersProps {
  calloutData: CoachCalloutData | null;
  onSendAnnouncements: () => void;
  onManageCoaches: () => void;
  isEmailDisabled?: boolean;
}

/**
 * Renders the coach-related callout banners above the groups list:
 * - "All groups matched" announcement banner (EC-34)
 * - "Awaiting contract signatures" warning banner (EC-36)
 */
export function GroupCalloutBanners({
  calloutData,
  onSendAnnouncements,
  onManageCoaches,
  isEmailDisabled,
}: GroupCalloutBannersProps) {
  if (!calloutData?.callout_type) return null;

  return (
    <>
      {calloutData.callout_type === 'announcement' && (
        <Alert
          variant="filled"
          color="dark"
          title="All groups have been matched with a coach!"
          icon={<CheckCircle size={20} />}
          styles={{
            root: { backgroundColor: '#28463A', border: 'none' },
            title: { color: 'white' },
            icon: { color: 'white' },
            message: { color: 'white' },
          }}
        >
          <Group justify="space-between" align="center">
            <Text size="sm" c="white">
              {calloutData.total_groups} group{calloutData.total_groups !== 1 ? 's' : ''} with{' '}
              {calloutData.total_sessions} session{calloutData.total_sessions !== 1 ? 's' : ''} are
              ready. Send email announcements to inform coaches.
            </Text>
            <Tooltip
              label="This email feature has been turned off for this cohort. To enable it, go to Cohort Settings > Email Settings."
              disabled={!isEmailDisabled}
              multiline
              w={280}
            >
              <Button
                size="sm"
                variant="white"
                color="#28463A"
                leftSection={<Send size={14} />}
                onClick={onSendAnnouncements}
                disabled={isEmailDisabled}
              >
                Finalize & Send Announcements
              </Button>
            </Tooltip>
          </Group>
        </Alert>
      )}

      {calloutData.callout_type === 'contract' && (
        <Alert
          variant="light"
          color="orange"
          title="Awaiting Contract Signatures"
          icon={<AlertCircle size={20} />}
          styles={{
            root: { backgroundColor: '#FEF3E7', border: '1px solid #FED7AA' },
            title: { color: '#E07B4C' },
            icon: { color: '#E07B4C' },
            message: { color: '#E07B4C' },
          }}
        >
          <Group justify="space-between" align="center">
            <Text size="sm" c="#E07B4C">
              {calloutData.coaches_awaiting_contract} coach
              {calloutData.coaches_awaiting_contract !== 1 ? 'es' : ''} need contracts signed —{' '}
              <strong>{calloutData.coaches_awaiting_names.join(', ')}</strong>
            </Text>
            <Button
              size="sm"
              styles={{
                root: { backgroundColor: '#E07B4C', color: 'white', border: 'none' },
                label: { color: 'white' },
              }}
              onClick={onManageCoaches}
            >
              Manage Coaches
            </Button>
          </Group>
        </Alert>
      )}
    </>
  );
}
